"use client";

import React, { useState, useMemo, useCallback, useEffect } from "react";
import Link from "next/link";
import { motion, AnimatePresence } from "framer-motion";
import Section from "@/components/public/Section";
import { DarkHeroHeader } from "@/components/public/DarkHeroHeader";
import { ArticleContent } from "@/components/public/ArticleContent";
import {
  Search,
  ArrowRight,
  ChevronRight,
  ChevronDown,
  CreditCard,
  Sparkles,
  Clock,
  Truck,
  BedDouble,
  Wrench,
  ShoppingCart,
  FileText,
  X,
  ExternalLink,
  Menu,
} from "lucide-react";
import {
  allArticles,
  allCategories,
  searchArticles,
  getArticlesByCategory,
  type FaqArticle,
} from "@/lib/faqUtils";

// ─── Category metadata ────────────────────────────────────────────────────────

const CATEGORY_META: Record<
  string,
  { icon: React.ReactNode; color: string }
> = {
  accounts:          { icon: <CreditCard className="w-4 h-4" />,   color: "#8b5cf6" },
  cleaning:          { icon: <Sparkles className="w-4 h-4" />,     color: "#06b6d4" },
  "cut-off":         { icon: <Clock className="w-4 h-4" />,        color: "#f59e0b" },
  deliveries:        { icon: <Truck className="w-4 h-4" />,        color: "#10b981" },
  linen:             { icon: <BedDouble className="w-4 h-4" />,    color: "#ec4899" },
  maintenance:       { icon: <Wrench className="w-4 h-4" />,       color: "#f97316" },
  "ordering-system": { icon: <ShoppingCart className="w-4 h-4" />, color: "#6366f1" },
};

// ─── Props ────────────────────────────────────────────────────────────────────

interface HelpCenterTemplateProps {
  /** Pre-select a category on load (from /help-center/[category] route) */
  initialCategory?: string;
}

// ─── Component ────────────────────────────────────────────────────────────────

export function HelpCenterTemplate({ initialCategory }: HelpCenterTemplateProps = {}) {
  const [activeCategory, setActiveCategory] = useState(
    initialCategory && allCategories.some((c) => c.id === initialCategory)
      ? initialCategory
      : allCategories[0]?.id ?? ""
  );
  const [searchQuery, setSearchQuery] = useState("");
  const [expandedSlug, setExpandedSlug] = useState<string | null>(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  // Search across all categories
  const searchResults = useMemo(() => {
    if (!searchQuery.trim()) return null;
    return searchArticles(searchQuery);
  }, [searchQuery]);

  // Articles for active category, grouped
  const { ungrouped, groups } = useMemo(() => {
    const articles = getArticlesByCategory(activeCategory);
    const grps = new Map<string, FaqArticle[]>();
    const ung: FaqArticle[] = [];

    for (const article of articles) {
      if (article.subcategoryName) {
        const existing = grps.get(article.subcategoryName);
        if (existing) existing.push(article);
        else grps.set(article.subcategoryName, [article]);
      } else {
        ung.push(article);
      }
    }
    return { ungrouped: ung, groups: grps };
  }, [activeCategory]);

  const activeCategoryData = useMemo(
    () => allCategories.find((c) => c.id === activeCategory),
    [activeCategory]
  );

  const accentColor = CATEGORY_META[activeCategory]?.color || "#a855f7";

  const handleCategoryChange = useCallback((id: string) => {
    setActiveCategory(id);
    setExpandedSlug(null);
    setSearchQuery("");
    setMobileMenuOpen(false);
  }, []);

  const toggleArticle = useCallback((slug: string) => {
    setExpandedSlug((prev) => (prev === slug ? null : slug));
  }, []);

  // Close mobile menu on resize
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 1024) setMobileMenuOpen(false);
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  return (
    <>
      {/* ── Hero ────────────────────────────────────────────────────────── */}
      <DarkHeroHeader
        eyebrow="Help Centre"
        title="How can we help?"
        description="Search our knowledge base or browse by category."
      >
        <div className="mt-8 max-w-xl">
          <div className="relative">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-white/30" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search help articles…"
              className="w-full pl-12 pr-10 py-3.5 bg-white/5 border border-white/10 rounded-xl text-sm text-white placeholder:text-white/30 focus:outline-none focus:ring-2 focus:ring-purple-500/50 focus:ring-offset-2 focus:ring-offset-[var(--homepage-primary,#0d2434)] focus:border-transparent focus:bg-white/10 transition-all"
            />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery("")}
                className="absolute right-3 top-1/2 -translate-y-1/2 p-1 rounded-full hover:bg-white/10 transition-colors"
              >
                <X className="w-4 h-4 text-white/40" />
              </button>
            )}
          </div>
          <p className="text-xs text-white/30 mt-2.5">
            {allArticles.length} articles across {allCategories.length}{" "}
            categories
          </p>
        </div>
      </DarkHeroHeader>

      {/* ── Main content ────────────────────────────────────────────────── */}
      <Section spacing="none" background="white">
        {/* Mobile category toggle bar */}
        <div className="lg:hidden border-b border-gray-100">
          <div className="px-4 sm:px-6">
            <button
              onClick={() => setMobileMenuOpen((v) => !v)}
              className="flex items-center justify-between w-full py-3.5"
            >
              <div className="flex items-center gap-2.5">
                <span
                  className="w-7 h-7 rounded-md flex items-center justify-center text-white"
                  style={{ backgroundColor: accentColor }}
                >
                  {CATEGORY_META[activeCategory]?.icon || <FileText className="w-3.5 h-3.5" />}
                </span>
                <span className="text-sm font-semibold text-gray-900 font-satoshi">
                  {activeCategoryData?.name || "Categories"}
                </span>
                <span className="text-xs text-gray-400">
                  ({activeCategoryData?.articleCount || 0})
                </span>
              </div>
              <Menu className={`w-5 h-5 text-gray-400 transition-transform ${mobileMenuOpen ? "rotate-90" : ""}`} />
            </button>
          </div>

          {/* Mobile category drawer */}
          <AnimatePresence>
            {mobileMenuOpen && (
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: "auto", opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                transition={{ duration: 0.2 }}
                className="overflow-hidden border-t border-gray-100"
              >
                <div className="px-4 sm:px-6 py-3 space-y-0.5 bg-gray-50/50">
                  {allCategories.map((c) => {
                    const meta = CATEGORY_META[c.id];
                    const isActive = c.id === activeCategory;
                    return (
                      <button
                        key={c.id}
                        onClick={() => handleCategoryChange(c.id)}
                        className={`flex items-center gap-2.5 w-full px-3 py-2.5 rounded-lg text-sm transition-all text-left ${
                          isActive
                            ? "bg-gray-900 text-white font-medium"
                            : "text-gray-600 hover:bg-white hover:text-gray-900"
                        }`}
                      >
                        <span className={isActive ? "text-white/70" : "text-gray-400"}>
                          {meta?.icon || <FileText className="w-4 h-4" />}
                        </span>
                        <span className="flex-1">{c.name}</span>
                        <span className={`text-xs ${isActive ? "text-white/50" : "text-gray-400"}`}>
                          {c.articleCount}
                        </span>
                      </button>
                    );
                  })}
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {/* Desktop layout: sidebar + content */}
        <div className="w-full">
          <div className="flex min-h-[60vh]">
            {/* ── Desktop sidebar ─────────────────────────────────────── */}
            <aside className="hidden lg:block w-56 shrink-0 border-r border-gray-100 pr-6 py-10">
              <nav className="space-y-1">
                <p className="text-[11px] font-semibold uppercase tracking-wider text-gray-400 mb-3 px-3">
                  Categories
                </p>
                {allCategories.map((c) => {
                  const meta = CATEGORY_META[c.id];
                  const isActive = c.id === activeCategory;
                  return (
                    <button
                      key={c.id}
                      onClick={() => handleCategoryChange(c.id)}
                      className={`flex items-center gap-2.5 w-full px-3 py-2 rounded-lg text-sm transition-all text-left ${
                        isActive
                          ? "bg-gray-900 text-white font-medium"
                          : "text-gray-600 hover:bg-gray-50 hover:text-gray-900"
                      }`}
                    >
                      <span className={isActive ? "text-white/70" : "text-gray-400"}>
                        {meta?.icon || <FileText className="w-4 h-4" />}
                      </span>
                      <span className="flex-1 truncate">{c.name}</span>
                      <span className={`text-xs tabular-nums ${isActive ? "text-white/50" : "text-gray-400"}`}>
                        {c.articleCount}
                      </span>
                    </button>
                  );
                })}

                {/* Contact CTA in sidebar */}
                <div className="pt-6 mt-6 border-t border-gray-100">
                  <Link
                    href="/contact"
                    className="flex items-center gap-2 text-sm text-gray-400 hover:text-purple-600 transition-colors px-3"
                  >
                    Contact Support
                    <ArrowRight className="w-3 h-3" />
                  </Link>
                </div>
              </nav>
            </aside>

            {/* ── Content area ─────────────────────────────────────────── */}
            <div className="flex-1 min-w-0 lg:pl-10 py-8 lg:py-10">
              {/* Search results mode */}
              {searchResults !== null ? (
                <div>
                  <div className="mb-6">
                    <p className="text-sm text-gray-500">
                      {searchResults.length > 0
                        ? `${searchResults.length} result${searchResults.length !== 1 ? "s" : ""}`
                        : "No results"}{" "}
                      for &ldquo;{searchQuery}&rdquo;
                    </p>
                    {searchResults.length === 0 && (
                      <p className="text-sm text-gray-400 mt-1">
                        Try a different term or{" "}
                        <button
                          onClick={() => setSearchQuery("")}
                          className="text-purple-600 hover:underline"
                        >
                          browse categories
                        </button>
                        .
                      </p>
                    )}
                  </div>

                  <div className="divide-y divide-gray-100">
                    {searchResults.map((article) => (
                      <SearchAccordionItem
                        key={`${article.categoryId}-${article.slug}`}
                        article={article}
                        isExpanded={expandedSlug === article.slug}
                        onToggle={() => toggleArticle(article.slug)}
                      />
                    ))}
                  </div>
                </div>
              ) : (
                /* Category browse mode */
                <div>
                  {/* Category heading */}
                  <div className="mb-8">
                    <div className="flex items-center gap-3 mb-1.5">
                      <div
                        className="w-8 h-8 rounded-lg flex items-center justify-center text-white"
                        style={{ backgroundColor: accentColor }}
                      >
                        {CATEGORY_META[activeCategory]?.icon || <FileText className="w-4 h-4" />}
                      </div>
                      <h2 className="text-xl sm:text-2xl font-satoshi font-bold text-gray-900 tracking-tight">
                        {activeCategoryData?.name}
                      </h2>
                    </div>
                    <p className="text-sm text-gray-400 ml-11">
                      {activeCategoryData?.articleCount} article
                      {(activeCategoryData?.articleCount ?? 0) !== 1 ? "s" : ""}
                    </p>
                  </div>

                  {/* Ungrouped articles */}
                  {ungrouped.length > 0 && (
                    <div className="mb-8">
                      <div className="divide-y divide-gray-100">
                        {ungrouped.map((article) => (
                          <AccordionItem
                            key={article.slug}
                            article={article}
                            isExpanded={expandedSlug === article.slug}
                            onToggle={() => toggleArticle(article.slug)}
                            accentColor={accentColor}
                          />
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Subcategory groups */}
                  {Array.from(groups.entries()).map(([subName, subArticles]) => (
                    <div key={subName} className="mb-8">
                      <div className="flex items-center gap-2 mb-3 mt-2">
                        <div
                          className="w-1 h-4 rounded-full"
                          style={{ backgroundColor: accentColor }}
                        />
                        <h3 className="text-sm font-bold text-gray-900 font-satoshi uppercase tracking-wide">
                          {subName}
                        </h3>
                      </div>
                      <div className="divide-y divide-gray-100">
                        {subArticles.map((article) => (
                          <AccordionItem
                            key={article.slug}
                            article={article}
                            isExpanded={expandedSlug === article.slug}
                            onToggle={() => toggleArticle(article.slug)}
                            accentColor={accentColor}
                          />
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </Section>

      {/* ── Bottom CTA ──────────────────────────────────────────────────── */}
      <Section spacing="relaxed" bgColor="var(--homepage-primary, #0d2434)">
        <div className="text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            viewport={{ once: true }}
          >
            <h2 className="text-2xl sm:text-3xl font-satoshi font-bold text-white mb-3 tracking-tight">
              Can&apos;t find what you need?
            </h2>
            <p className="text-base text-white/50 leading-relaxed mb-8 max-w-md mx-auto">
              Our team is here to help.
            </p>
            <Link
              href="/contact"
              className="inline-flex items-center gap-2 px-6 py-3 rounded-xl font-semibold text-white text-sm transition-all hover:brightness-110"
              style={{ backgroundColor: "#a855f7" }}
            >
              Contact Support
              <ArrowRight className="w-4 h-4" />
            </Link>
          </motion.div>
        </div>
      </Section>
    </>
  );
}

// ─── Accordion item (category browse) ─────────────────────────────────────────

function AccordionItem({
  article,
  isExpanded,
  onToggle,
  accentColor,
}: {
  article: FaqArticle;
  isExpanded: boolean;
  onToggle: () => void;
  accentColor: string;
}) {
  return (
    <div className="group">
      <button
        onClick={onToggle}
        className="flex items-center justify-between gap-4 w-full text-left py-4 transition-colors"
      >
        <p
          className={`text-[15px] font-medium font-satoshi leading-snug transition-colors ${
            isExpanded ? "text-gray-900" : "text-gray-700 group-hover:text-gray-900"
          }`}
        >
          {article.question}
        </p>
        <ChevronDown
          className={`w-4 h-4 shrink-0 transition-transform duration-200 ${
            isExpanded ? "rotate-180 text-gray-900" : "text-gray-400"
          }`}
        />
      </button>

      <AnimatePresence initial={false}>
        {isExpanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.25, ease: "easeInOut" }}
            className="overflow-hidden"
          >
            <div className="pb-5 pr-8">
              {/* Accent bar */}
              <div className="flex gap-3">
                <div
                  className="w-0.5 shrink-0 rounded-full"
                  style={{ backgroundColor: accentColor }}
                />
                <div className="min-w-0 flex-1">
                  <ArticleContent answer={article.answer} images={article.images} />

                  {/* Direct link */}
                  <Link
                    href={`/help-center/${article.categoryId}/${article.slug}`}
                    className="inline-flex items-center gap-1.5 text-xs font-medium mt-4 transition-opacity hover:opacity-80"
                    style={{ color: accentColor }}
                  >
                    Open full article <ExternalLink className="w-3 h-3" />
                  </Link>
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

// ─── Search result accordion item ─────────────────────────────────────────────

function SearchAccordionItem({
  article,
  isExpanded,
  onToggle,
}: {
  article: FaqArticle;
  isExpanded: boolean;
  onToggle: () => void;
}) {
  const meta = CATEGORY_META[article.categoryId];
  const accentColor = meta?.color || "#a855f7";

  return (
    <div className="group">
      <button
        onClick={onToggle}
        className="flex items-start gap-3 w-full text-left py-4 transition-colors"
      >
        <div
          className="w-7 h-7 rounded-md flex items-center justify-center text-white shrink-0 mt-0.5"
          style={{ backgroundColor: accentColor }}
        >
          {meta?.icon || <FileText className="w-3.5 h-3.5" />}
        </div>
        <div className="min-w-0 flex-1">
          <p
            className={`text-[15px] font-medium font-satoshi leading-snug transition-colors ${
              isExpanded ? "text-gray-900" : "text-gray-700 group-hover:text-gray-900"
            }`}
          >
            {article.question}
          </p>
          <span className="text-xs text-gray-400 mt-0.5 inline-block">
            {article.categoryName}
            {article.subcategoryName && ` › ${article.subcategoryName}`}
          </span>
        </div>
        <ChevronDown
          className={`w-4 h-4 shrink-0 mt-1 transition-transform duration-200 ${
            isExpanded ? "rotate-180 text-gray-900" : "text-gray-400"
          }`}
        />
      </button>

      <AnimatePresence initial={false}>
        {isExpanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.25, ease: "easeInOut" }}
            className="overflow-hidden"
          >
            <div className="pb-5 pl-10 pr-8">
              <div className="flex gap-3">
                <div
                  className="w-0.5 shrink-0 rounded-full"
                  style={{ backgroundColor: accentColor }}
                />
                <div className="min-w-0 flex-1">
                  <ArticleContent answer={article.answer} images={article.images} />
                  <Link
                    href={`/help-center/${article.categoryId}/${article.slug}`}
                    className="inline-flex items-center gap-1.5 text-xs font-medium mt-4 transition-opacity hover:opacity-80"
                    style={{ color: accentColor }}
                  >
                    Open full article <ExternalLink className="w-3 h-3" />
                  </Link>
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

export default HelpCenterTemplate;
